import CreatableSelect from "react-select/creatable"
import { useMemo } from "react"
import { Spinner, Label } from "reactstrap"
import { getIn, setIn } from "formik"

const FormikCreatableSelect = ({
  options,
  name,
  formik,
  label,
  placeholder,
  loading = false,
  required = false,
  isMulti = false,
  optionBgColor,
  selectedBgColor = "#d1e7dd",
  onCreateOption,
  onChange,
}) => {
  // ✅ Get the current value from Formik using the name path
  const value = useMemo(() => getIn(formik.values, name), [formik.values, name])

  // ✅ Get the error for this field
  const error = useMemo(() => getIn(formik.errors, name), [formik.errors, name])
  const touched = useMemo(
    () => getIn(formik.touched, name),
    [formik.touched, name]
  )

  // ✅ Handle select change
  const handleChange = selected => {
    if (isMulti) {
      const values = selected ? selected.map(opt => opt.value) : []
      formik.setFieldValue(name, values)

      onChange?.(values, selected) // 👈 notify parent
    } else {
      const value = selected ? selected.value : ""
      formik.setFieldValue(name, value)

      onChange?.(value, selected) // 👈 notify parent
    }
  }

  // ✅ Handle create option
  const handleCreate = inputValue => {
    if (isMulti) {
      const currentValues = value || []
      formik.setFieldValue(name, [...currentValues, inputValue])
    } else {
      formik.setFieldValue(name, inputValue)
    }

    onCreateOption?.(inputValue)
    onChange?.(inputValue, { value: inputValue, label: inputValue }) // 👈 important
  }

  // ✅ Resolve selected value(s)
  const selectedValue = useMemo(() => {
    if (isMulti) {
      const values = value || []
      return options.filter(opt => values.includes(opt.value))
    } else {
      if (!value) return null

      // Initial value may already be a resolved option object ({ label, value })
      // rather than a plain scalar id.
      const rawValue =
        typeof value === "object" && value !== null && "value" in value
          ? value.value
          : value

      // First try to find in options
      const option = options.find(opt => String(opt.value) === String(rawValue))
      if (option) return option

      // If not found, it might be a newly created value
      // Create a temporary option for display
      const rawLabel =
        typeof value === "object" && value !== null && "label" in value
          ? value.label
          : rawValue
      return { value: rawValue, label: rawLabel }
    }
  }, [value, options, isMulti])

  // ✅ Handle blur
  const handleBlur = () => {
    // Set touched for this specific field
    const newTouched = setIn(formik.touched, name, true)
    formik.setTouched(newTouched)
  }

  // 🎨 Styles — theme-aware (works in both light and dark admin themes).
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? selectedBgColor
        : state.isFocused
        ? optionBgColor || "var(--bs-tertiary-bg)"
        : "transparent",
      color: state.isSelected ? "#212529" : "var(--bs-body-color)",
    }),
    control: (provided, state) => ({
      ...provided,
      minHeight: "38px",
      borderColor: touched && error ? "red" : "var(--bs-border-color)",
      boxShadow: "none",
      backgroundColor: state.isDisabled
        ? "var(--bs-secondary-bg)"
        : "var(--bs-body-bg)",
    }),
    menu: provided => ({
      ...provided,
      zIndex: 9999,
      backgroundColor: "var(--bs-body-bg)",
    }),
    input: provided => ({
      ...provided,
      backgroundColor: "transparent",
      color: "var(--bs-body-color)",
    }),
    singleValue: provided => ({
      ...provided,
      backgroundColor: "transparent",
      color: "var(--bs-body-color)",
    }),
    placeholder: provided => ({ ...provided, color: "var(--bs-secondary-color)" }),
    multiValue: provided => ({ ...provided, backgroundColor: "var(--bs-tertiary-bg)" }),
    multiValueLabel: provided => ({
      ...provided,
      backgroundColor: "transparent",
      color: "var(--bs-body-color)",
    }),
    multiValueRemove: provided => ({ ...provided, backgroundColor: "transparent" }),
  }

  const loadingMessage = () => (
    <div className="text-center py-2">
      <Spinner size="sm" /> Loading...
    </div>
  )

  return (
    <div className="mb-3">
      {label && (
        <Label className="form-label">
          {label} {required && <span className="text-danger">*</span>}
        </Label>
      )}

      <CreatableSelect
        classNamePrefix="select"
        isMulti={isMulti}
        isClearable
        inputId={name}
        options={options}
        value={selectedValue}
        placeholder={placeholder}
        onChange={handleChange}
        onCreateOption={handleCreate}
        onBlur={handleBlur}
        styles={customStyles}
        isDisabled={loading}
        noOptionsMessage={() => (loading ? loadingMessage() : "No options")}
      />

      {touched && error && (
        <div className="text-danger small mt-1">{error}</div>
      )}
    </div>
  )
}

export default FormikCreatableSelect
